// Units of measure: common presets (with the spellings shops use) and matching
// of free text to a business's units. Used by Settings, the product form and import.
import {
  defaultOtherPrice,
  mulDivRound,
  toStockQty,
  type TwoUnits,
  type UnitChoice,
} from '@ims/core'
import type { RowOf } from '@ims/data'

type UnitRow = RowOf<'units'>

export interface UnitPreset {
  symbol: string
  name: string
  allowsDecimal: boolean
  aliases: string[]
}

export const UNIT_PRESETS: UnitPreset[] = [
  {
    symbol: 'pcs',
    name: 'Pieces',
    allowsDecimal: false,
    aliases: [
      'pc',
      'piece',
      'pieces',
      'nos',
      'no',
      'each',
      'ea',
      'unit',
      'units',
      'wata',
      'वटा',
      'थान',
      'गोटा',
    ],
  },
  {
    symbol: 'kg',
    name: 'Kilogram',
    allowsDecimal: true,
    aliases: ['kgs', 'kilo', 'kilos', 'kilogram', 'kilograms', 'केजी', 'किलो'],
  },
  {
    symbol: 'g',
    name: 'Gram',
    allowsDecimal: true,
    aliases: ['gm', 'gms', 'gram', 'grams', 'gr', 'ग्राम'],
  },
  {
    symbol: 'l',
    name: 'Litre',
    allowsDecimal: true,
    aliases: ['ltr', 'ltrs', 'lt', 'litre', 'litres', 'liter', 'liters', 'लिटर'],
  },
  {
    symbol: 'ml',
    name: 'Millilitre',
    allowsDecimal: true,
    aliases: ['millilitre', 'milliliter', 'mls'],
  },
  {
    symbol: 'm',
    name: 'Metre',
    allowsDecimal: true,
    aliases: ['mtr', 'mtrs', 'metre', 'meter', 'metres', 'meters', 'मिटर'],
  },
  { symbol: 'dozen', name: 'Dozen', allowsDecimal: false, aliases: ['dz', 'doz', 'dzn', 'दर्जन'] },
  { symbol: 'box', name: 'Box', allowsDecimal: false, aliases: ['boxes', 'bx', 'बाकस'] },
  {
    symbol: 'pack',
    name: 'Pack',
    allowsDecimal: false,
    aliases: ['packs', 'pkt', 'pkts', 'packet', 'packets', 'प्याकेट'],
  },
  { symbol: 'pair', name: 'Pair', allowsDecimal: false, aliases: ['pairs', 'pr', 'जोडी'] },
  { symbol: 'set', name: 'Set', allowsDecimal: false, aliases: ['sets'] },
  { symbol: 'bottle', name: 'Bottle', allowsDecimal: false, aliases: ['bottles', 'btl', 'बोतल'] },
  { symbol: 'bag', name: 'Bag', allowsDecimal: false, aliases: ['bags', 'sack', 'bora', 'बोरा'] },
  { symbol: 'roll', name: 'Roll', allowsDecimal: false, aliases: ['rolls'] },
  { symbol: 'carton', name: 'Carton', allowsDecimal: false, aliases: ['cartons', 'ctn', 'ctns'] },
  // Medicines: a strip of tablets or capsules, a sachet of powder.
  {
    symbol: 'strip',
    name: 'Strip',
    allowsDecimal: false,
    aliases: ['strips', 'stp', 'पत्ता', 'स्ट्रिप'],
  },
  {
    symbol: 'tab',
    name: 'Tablet',
    allowsDecimal: false,
    aliases: ['tabs', 'tablet', 'tablets', 'ट्याब्लेट'],
  },
  {
    symbol: 'cap',
    name: 'Capsule',
    allowsDecimal: false,
    aliases: ['caps', 'capsule', 'capsules', 'क्याप्सुल'],
  },
  { symbol: 'sachet', name: 'Sachet', allowsDecimal: false, aliases: ['sachets', 'pouch'] },
  { symbol: 'tola', name: 'Tola', allowsDecimal: true, aliases: ['tolas', 'तोला'] },
  // Labour and services (workshops): sold by time, by the job or by the visit.
  { symbol: 'hr', name: 'Hour', allowsDecimal: true, aliases: ['hrs', 'hour', 'hours', 'घण्टा'] },
  { symbol: 'job', name: 'Job', allowsDecimal: false, aliases: ['jobs', 'service', 'काम'] },
  { symbol: 'visit', name: 'Visit', allowsDecimal: false, aliases: ['visits', 'trip', 'trips'] },
  {
    symbol: 'km',
    name: 'Kilometre',
    allowsDecimal: true,
    aliases: ['kms', 'kilometre', 'kilometer', 'kilometres', 'kilometers', 'किमी'],
  },
  // Lengths and areas still sold the old way: pipe by the foot, glass by the square foot.
  { symbol: 'ft', name: 'Foot', allowsDecimal: true, aliases: ['feet', 'foot', 'फिट'] },
  { symbol: 'inch', name: 'Inch', allowsDecimal: true, aliases: ['in', 'inches', 'इन्च'] },
  {
    symbol: 'sqft',
    name: 'Square foot',
    allowsDecimal: true,
    aliases: ['sq ft', 'sq.ft', 'square feet', 'sft', 'वर्ग फिट'],
  },
  {
    symbol: 'mm',
    name: 'Millimetre',
    allowsDecimal: true,
    aliases: ['millimetre', 'millimeter', 'millimetres', 'millimeters'],
  },
  { symbol: 'can', name: 'Can', allowsDecimal: false, aliases: ['cans', 'tin', 'tins'] },
  { symbol: 'tube', name: 'Tube', allowsDecimal: false, aliases: ['tubes'] },
  { symbol: 'drum', name: 'Drum', allowsDecimal: false, aliases: ['drums', 'barrel', 'barrels'] },
  { symbol: 'coil', name: 'Coil', allowsDecimal: false, aliases: ['coils'] },
]

const key = (value: string) => value.trim().toLowerCase().replace(/\.$/, '')

/** The preset a spelling belongs to ("pkt" → Pack), or null. */
export function presetFor(raw: string): UnitPreset | null {
  const k = key(raw)
  return (
    UNIT_PRESETS.find(
      (p) => p.symbol === k || p.name.toLowerCase() === k || p.aliases.includes(k),
    ) ?? null
  )
}

/** Whether a product's Unit is the unit typed for two units, in any common spelling. */
function sameUnit(unit: Pick<UnitRow, 'name' | 'symbol'>, typed: string): boolean {
  const t = key(typed)
  if (!t) return false
  if (key(unit.name) === t || key(unit.symbol) === t) return true
  const preset = presetFor(typed)
  return (
    !!preset &&
    (presetFor(unit.symbol)?.symbol === preset.symbol ||
      presetFor(unit.name)?.symbol === preset.symbol)
  )
}

/** The typed unit a product's Unit already names, if it names one. */
export function unitMatch(
  unit: Pick<UnitRow, 'name' | 'symbol'> | null | undefined,
  larger: string,
  smaller: string,
): 'larger' | 'smaller' | null {
  if (!unit) return null
  if (sameUnit(unit, smaller)) return 'smaller'
  return sameUnit(unit, larger) ? 'larger' : null
}

/**
 * Which unit a product's price and stock are per (docs/plans/two-units.md). Null: nothing says,
 * and the user must answer before saving.
 */
export function stockUnitAnswer(a: {
  isNew: boolean
  used: boolean
  saved: 'larger' | 'smaller' | null
  picked: 'larger' | 'smaller' | null
  match: 'larger' | 'smaller' | null
}): 'larger' | 'smaller' | null {
  if (a.used && a.saved) return a.saved
  if (a.isNew) return a.picked ?? 'smaller'
  return a.picked ?? a.saved ?? a.match
}

/** A ready-made pair of units: one tap fills "1 [larger] = [count] [smaller]". */
export interface QuickPair {
  label: string
  /** Preset spellings, matched to the shop's own units with `findUnit`. */
  larger: string
  count: number
  smaller: string
}

/** The quick picks a business type is offered (board ProdTwoUnits). */
export function quickPairs(businessType: string | null | undefined): QuickPair[] {
  switch (businessType) {
    case 'pharmacy':
      return [
        { label: 'Strip of 10 tablets', larger: 'Strip', count: 10, smaller: 'Tablet' },
        { label: 'Strip of 15 capsules', larger: 'Strip', count: 15, smaller: 'Capsule' },
        { label: 'Box of 10 strips', larger: 'Box', count: 10, smaller: 'Strip' },
        { label: 'Box of 100 tablets', larger: 'Box', count: 100, smaller: 'Tablet' },
      ]
    case 'agrovet':
      return [
        { label: 'Sack of 50 kg', larger: 'Sack', count: 50, smaller: 'Kilogram' },
        { label: 'Bottle of 1000 ml', larger: 'Bottle', count: 1000, smaller: 'Millilitre' },
        { label: 'Packet of 500 g', larger: 'Pack', count: 500, smaller: 'Gram' },
      ]
    case 'apparel':
      return [
        { label: 'Dozen of 12 pieces', larger: 'Dozen', count: 12, smaller: 'Pieces' },
        { label: 'Set of 3 pieces', larger: 'Set', count: 3, smaller: 'Pieces' },
        { label: 'Box of 6 pairs', larger: 'Box', count: 6, smaller: 'Pair' },
      ]
    case 'hardware':
      return [
        { label: 'Box of 100 pieces', larger: 'Box', count: 100, smaller: 'Pieces' },
        { label: 'Packet of 10 pieces', larger: 'Pack', count: 10, smaller: 'Pieces' },
        { label: 'Carton of 12 boxes', larger: 'Carton', count: 12, smaller: 'Box' },
      ]
    default:
      return [
        { label: 'Carton of 12 packets', larger: 'Carton', count: 12, smaller: 'Pack' },
        { label: 'Dozen of 12 pieces', larger: 'Dozen', count: 12, smaller: 'Pieces' },
        { label: 'Box of 24 pieces', larger: 'Box', count: 24, smaller: 'Pieces' },
      ]
  }
}

/** Stock typed as "3 strips and 4 tablets", in the unit stock is counted in. */
export function openingFromParts(largerMilli: number, smallerMilli: number, u: TwoUnits): number {
  return toStockQty(largerMilli, 'larger', u) + toStockQty(smallerMilli, 'smaller', u)
}

export interface TwoUnitsCheckInput {
  units: UnitRow[]
  larger: string
  smaller: string
  count: number | null
  stockIn: UnitChoice | null
  /** The product's price, per the unit stock is counted in. */
  stockPrice: number | null
  /** A price typed for the other unit; null follows the stock price. */
  otherPrice: number | null
  mrp: number | null
  /** The unit a saved product's own Unit names, if it names one of the two. */
  unitSays: UnitChoice | null
}

export interface TwoUnitsChecks {
  /** These stop Save: what they describe can't be stored. */
  blocking: { field: 'units' | 'count' | 'stockIn'; text: string }[]
  /** These only warn: a shop may charge more for a loose one. */
  warnings: { field: 'other' | 'mrp' | 'stockIn'; text: string }[]
}

/** "Strip" → "Strips", "Box" → "Boxes"; a name already plural ("Pieces") stays. */
export const pluralUnit = (name: string) =>
  /s$/i.test(name) ? name : /(x|ch|sh)$/i.test(name) ? `${name}es` : `${name}s`
const plural = pluralUnit

/** What is wrong with two units as they stand, beside the box it is about (board ProdUnitWarn). */
export function twoUnitsChecks(
  a: TwoUnitsCheckInput,
  money: (paisa: number) => string,
): TwoUnitsChecks {
  const out: TwoUnitsChecks = { blocking: [], warnings: [] }
  const larger = a.larger.trim()
  const smaller = a.smaller.trim()
  if (!larger || !smaller) {
    out.blocking.push({ field: 'units', text: 'Choose both units' })
  } else {
    const l = findUnit(a.units, larger)
    const s = findUnit(a.units, smaller)
    if ((l && s && l.id === s.id) || key(larger) === key(smaller)) {
      out.blocking.push({
        field: 'units',
        text: `Choose two different units: 1 ${larger} = ${a.count ?? 10} ${plural(smaller)} can’t be sold.`,
      })
    }
  }
  const count = a.count ?? 0
  const valid = count >= 2 && count <= 1000
  if (a.count !== null && !valid) {
    out.blocking.push({
      field: 'count',
      text: `From 2 to 1,000 ${plural(smaller || 'smaller unit').toLowerCase()} in a ${(larger || 'larger unit').toLowerCase()}.`,
    })
  } else if (a.count === null) {
    out.blocking.push({
      field: 'count',
      text: `Type how many ${plural(smaller || 'smaller units').toLowerCase()} are in one`,
    })
  }
  if (a.stockIn === null) {
    out.blocking.push({ field: 'stockIn', text: 'Choose which unit stock is counted in' })
  }
  if (!valid || a.stockIn === null || a.stockPrice === null || out.blocking.length) return out

  const u: TwoUnits = { larger_name: larger, smaller_name: smaller, count, stock_in: a.stockIn }
  const other = a.otherPrice ?? defaultOtherPrice(a.stockPrice, u)
  const largerPrice = a.stockIn === 'larger' ? a.stockPrice : other
  const smallerPrice = a.stockIn === 'smaller' ? a.stockPrice : other
  const share = mulDivRound(largerPrice, 1, count)
  if (a.otherPrice !== null && smallerPrice > share) {
    out.warnings.push({
      field: 'other',
      text: `${money(smallerPrice - share)} above the ${larger.toLowerCase()}’s share (${money(largerPrice)} ÷ ${count}). Fine if that is your price.`,
    })
  }
  if (a.mrp !== null) {
    // MRP is for the unit stock is counted in, like the selling price beside it.
    const mrpShare = a.stockIn === 'larger' ? Math.round(a.mrp / count) : a.mrp
    if (smallerPrice > mrpShare) {
      out.warnings.push({
        field: 'mrp',
        text: `${money(smallerPrice)} is above the MRP share of ${money(mrpShare)} a ${smaller.toLowerCase()}. You can still save.`,
      })
    }
  }
  // A typed price for the smaller unit shows the shop knows which is which.
  const unsure = a.unitSays === 'smaller' || (a.unitSays === null && a.otherPrice === null)
  if (a.stockIn === 'larger' && unsure && a.stockPrice > 0) {
    out.warnings.push({
      field: 'stockIn',
      text: `${money(a.stockPrice)} a ${larger.toLowerCase()} makes a ${smaller.toLowerCase()} ${money(other)}. If ${money(a.stockPrice)} is the price of one ${smaller.toLowerCase()}, count stock in ${plural(smaller)}.`,
    })
  }
  return out
}

/**
 * The business's unit for some text: its symbol or name, else any unit the
 * text's preset stands for ("ltr" finds the business's "l").
 */
export function findUnit(units: UnitRow[], raw: string): UnitRow | null {
  const k = key(raw)
  if (!k) return null
  const direct = units.find((u) => key(u.symbol) === k || key(u.name) === k)
  if (direct) return direct
  const preset = presetFor(k)
  if (!preset) return null
  const spellings = [preset.symbol, preset.name.toLowerCase(), ...preset.aliases]
  return (
    units.find((u) => spellings.includes(key(u.symbol)) || spellings.includes(key(u.name))) ?? null
  )
}

export interface NewUnitSpec {
  symbol: string
  name: string
  allowsDecimal: boolean
}

/** What to create for an unknown unit: a known preset, else the text itself. */
export function unitForNew(raw: string): NewUnitSpec {
  const preset = presetFor(raw)
  if (preset) {
    return { symbol: preset.symbol, name: preset.name, allowsDecimal: preset.allowsDecimal }
  }
  const text = raw.trim().replace(/\s+/g, ' ')
  return {
    symbol: text.toLowerCase().slice(0, 16),
    name: text.charAt(0).toUpperCase() + text.slice(1),
    allowsDecimal: false,
  }
}

/** Presets the business doesn't have yet (for quick-add). */
export function missingPresets(units: UnitRow[]): UnitPreset[] {
  return UNIT_PRESETS.filter((p) => !findUnit(units, p.symbol))
}
